import React, { useEffect, useMemo, useRef, useState } from "react";
import { useReportDateState } from "../hooks/useReportDateAccess";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSyncAlt } from "react-icons/fa";
import { downloadCSV, downloadExcel, downloadPDF, printThermal, printThermalViaUsb} from "../utils/exportHelpers";
import { formatStoredMoney } from "../utils/currency";
import ReportPageHeader from "../components/ReportPageHeader";
import ReportPeriodPicker from "../components/ReportPeriodPicker";
import ReportControlBar from "../components/ReportControlBar";
import { API_ENDPOINTS } from "../config/constants";
import { getOfflineMonthWiseItemReport } from "../offline/financeStore";
import { isFullOfflineModeEnabled } from "../utils/fullOfflineMode";
import { formatSaleWeight } from "../utils/weightedSales";
import { buildItemSalesReceiptText } from "../utils/categorySalesReport";
import {
  isDesktopApp,
  indiaDateKey,
  numberValue,
  extractOrderItems,
  getCachedOrderDetail,
  getLocalProductAndCategoryMaps,
  getCategoryNameFromItem,
  DESKTOP_CACHE_HEADER,
} from "../utils/offlineReportUtils";

const defaultData = (month) => ({
  month,
  restaurant: "",
  total_items: 0,
  total_quantity: 0,
  total_amount: 0,
  total_orders: 0,
  items: [],
});

const isWeightedItem = (item) =>
  item?.is_weight_line === true ||
  item?.is_weight_line === 1 ||
  item?.is_weight_line === "1" ||
  numberValue(item?.sold_weight_grams ?? item?.soldWeightGrams ?? 0) > 0 ||
  String(item?.weight_label ?? item?.weightLabel ?? "").trim() !== "";

const resolveItemWeightLabel = (item) => {
  const explicit = String(item?.weight_label ?? item?.weightLabel ?? "").trim();
  if (explicit) return explicit;
  const grams = numberValue(item?.sold_weight_grams ?? item?.soldWeightGrams ?? 0);
  if (grams > 0) return formatSaleWeight(grams);
  const quantity = numberValue(item?.total_quantity ?? item?.qty ?? item?.quantity ?? 0);
  return isWeightedItem(item) && quantity > 0 ? formatSaleWeight(quantity * 1000) : "";
};

const stripWeightSuffix = (name, weightLabel) => {
  const value = String(name || "Item").trim();
  const suffix = weightLabel ? ` · ${weightLabel}` : "";
  return suffix && value.endsWith(suffix)
    ? value.slice(0, -suffix.length).trim()
    : value;
};

const MonthWiseItemReportPage = () => {
  const [userId, setUserId] = useState(null);
  const [month, setMonth] = useReportDateState(currentMonthKey(), "month");
  const [data, setData] = useState(defaultData(currentMonthKey()));
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [, setSourceMessage] = useState("");
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const reportRef = useRef(null);
  const storedUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("userData") || "{}")?.data || {}; }
    catch { return {}; }
  }, []);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}").data;
    if (!user?.id) {
      toast.error("User not found. Please login again.");
      setLoading(false);
      return;
    }
    setUserId(user.id);
  }, []);

  useEffect(() => {
    if (userId) fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, month]);

  const fetchReport = async (opts = {}) => {
    if (!userId) return;
    opts.refresh ? setRefreshing(true) : setLoading(true);
    setSourceMessage("");
    try {
      if (isFullOfflineModeEnabled()) {
        const offlineReport = await getOfflineMonthWiseItemReport(userId, month);
        setData({
          ...defaultData(month),
          ...offlineReport,
          items: Array.isArray(offlineReport?.items) ? offlineReport.items : [],
        });
        setSourceMessage(offlineSourceMessage(offlineReport));
        setPage(0);
        return;
      }
      const res = await fetch(
        API_ENDPOINTS.REPORT_MONTH_WISE_ITEMS,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid: userId, month }),
        }
      );
      const json = await res.json();
      if (!res.ok || !json?.success) {
        const offlineReport = await buildOfflineMonthItemReport(userId, month);
        if (!offlineReport) throw new Error(json?.message || "Failed to load report");
        setData(offlineReport);
        setSourceMessage(offlineSourceMessage(offlineReport));
        setPage(0);
        return;
      }
      const liveOrCachedReport = {
        ...defaultData(month),
        ...json,
        items: Array.isArray(json?.items) ? json.items : [],
      };
      if (res.headers.get(DESKTOP_CACHE_HEADER) === "1") {
        const offlineReport = await buildOfflineMonthItemReport(userId, month);
        if (offlineReport?.items?.length || !liveOrCachedReport.items.length) {
          setData(offlineReport || liveOrCachedReport);
          setSourceMessage(
            offlineReport
              ? offlineSourceMessage(offlineReport)
              : "Offline mode — showing the last saved item report for this month."
          );
        } else {
          setData(liveOrCachedReport);
          setSourceMessage("Offline mode — showing the last saved item report for this month.");
        }
      } else {
        setData(liveOrCachedReport);
      }
      setPage(0);
    } catch (e) {
      try {
        const offlineReport = await buildOfflineMonthItemReport(userId, month);
        if (offlineReport) {
          setData(offlineReport);
          setSourceMessage(offlineSourceMessage(offlineReport));
          setPage(0);
        } else {
          toast.error(e.message || "Unable to load report");
          setData(defaultData(month));
        }
      } catch (offlineError) {
        console.warn("Unable to build offline month-wise item report", offlineError);
        toast.error(e.message || "Unable to load report");
        setData(defaultData(month));
      }
    } finally {
      opts.refresh ? setRefreshing(false) : setLoading(false);
    }
  };

  const fmtINR = (n) => formatStoredMoney(n, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return { start, end, rows: data.items.slice(start, end) };
  }, [data.items, page]);

  const pageCount = Math.ceil((data.items.length || 0) / rowsPerPage);

  const printableItems = useMemo(
    () => data.items.map((item) => {
      const weightLabel = resolveItemWeightLabel(item);
      const itemName = stripWeightSuffix(item.item_name, weightLabel);
      return {
        ...item,
        item_name: weightLabel ? `${itemName} (${weightLabel})` : itemName,
      };
    }),
    [data.items]
  );

  const exportCsv = () => {
    const rows = data.items.map((it, idx) => [
      idx + 1,
      it.item_name,
      it.category_name || "Uncategorized",
      it.total_quantity,
      it.total_orders,
      it.total_amount,
    ]);
    downloadCSV(
      `month_wise_items_${month}.csv`,
      ["#", "Item", "Category", "Qty", "Orders", "Amount"],
      rows
    );
  };

  const exportExcel = () => {
    const rows = data.items.map((it, idx) => [
      idx + 1,
      it.item_name,
      it.category_name || "Uncategorized",
      it.total_quantity,
      it.total_orders,
      it.total_amount,
    ]);
    downloadExcel(
      `month_wise_items_${month}.xlsx`,
      ["#", "Item", "Category", "Qty", "Orders", "Amount"],
      rows
    );
  };

  const exportPdf = () => downloadPDF(reportRef.current, `month_wise_items_${month}.pdf`);

  const buildThermalReceipt = (lineWidth) =>
    buildItemSalesReceiptText(
      {
        restaurant:
          data.restaurant ||
          storedUser.restaurant_name ||
          storedUser.name ||
          "Bill Sarthi",
        addressLines: [
          storedUser.addressline1 || storedUser.address || "",
          storedUser.addressline2 || "",
          storedUser.addressline3 || "",
        ],
        registrationLines: [
          storedUser.customfield1 || "",
          storedUser.customfield2 || "",
          storedUser.customfield3 || "",
          storedUser.gstno ? `GSTIN: ${storedUser.gstno}` : "",
        ],
        periodLabel: getMonthPeriodLabel(month),
        printedAt: new Date(),
        items: printableItems,
      },
      lineWidth
    );

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2200} />

      <ReportPageHeader
        title="Month-wise Item Report"
        subtitle="Items sold during the selected month."
        stats={[
          { label: "Total Items", value: data.total_items || 0 },
          { label: "Total Qty", value: data.total_quantity || 0 },
          { label: "Total Orders", value: data.total_orders || 0 },
          { label: "Total Amount", value: fmtINR(data.total_amount) },
        ]}
      />

      <ReportControlBar>
        <ReportPeriodPicker
          month={month}
          onMonthChange={(m) => setMonth(m)}
          showPresets={false}
        />
        <PrimaryBtn onClick={() => fetchReport({ refresh: true })}>
          <FaSyncAlt /> {refreshing ? "Refreshing..." : "Refresh"}
        </PrimaryBtn>
      </ReportControlBar>


      <Card ref={reportRef}>
        {loading ? (
          <Center>Loading…</Center>
        ) : data.items.length === 0 ? (
          <Center>No data for this month.</Center>
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Particulars</th>
                  <th>Qty</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {paginated.rows.map((it, idx) => {
                  const weightLabel = resolveItemWeightLabel(it);
                  const itemName = stripWeightSuffix(it.item_name, weightLabel);
                  return (
                    <tr key={`${it.product_id || "item"}-${idx}`}>
                      <td>
                        <ItemName>{itemName}</ItemName>
                        {weightLabel ? <ItemWeightLabel>Weight: {weightLabel}</ItemWeightLabel> : null}
                      </td>
                      <NumberCell>{it.total_quantity}</NumberCell>
                      <NumberCell>{fmtINR(it.total_amount)}</NumberCell>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <GrandTotalRow>
                  <td>Grand Total</td>
                  <NumberCell>{data.total_quantity}</NumberCell>
                  <NumberCell>{fmtINR(data.total_amount)}</NumberCell>
                </GrandTotalRow>
              </tfoot>
            </Table>
          </TableWrap>
        )}

        <Pager>
          <PageBtn disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
            ‹ Prev
          </PageBtn>
          <PageInfo>
            Page <strong>{pageCount ? page + 1 : 0}</strong> of <strong>{pageCount}</strong>
          </PageInfo>
          <PageBtn disabled={page + 1 >= pageCount} onClick={() => setPage((p) => p + 1)}>
            Next ›
          </PageBtn>
        </Pager>

        <ActionsRow style={{ marginTop: 14 }}>
          <PrimaryBtn onClick={exportExcel}>Excel</PrimaryBtn>
          <PrimaryBtn onClick={exportPdf}>PDF</PrimaryBtn>
          <PrimaryBtn onClick={() => printThermal(
            reportRef.current,
            "Item Sales Register",
            { buildReceiptText: buildThermalReceipt }
          )}>
            Thermal Print
          </PrimaryBtn><PrimaryBtn onClick={() => printThermalViaUsb(
            reportRef.current,
            "Item Sales Register",
            { buildReceiptText: buildThermalReceipt }
          )}>
            Thermal Print via USB
          </PrimaryBtn>
          <PrimaryBtn onClick={exportCsv}>CSV</PrimaryBtn>
        </ActionsRow>
      </Card>
    </Page>
  );
};

export default MonthWiseItemReportPage;

const currentMonthKey = () => toMonthKey(new Date());
const toMonthKey = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};
const displayDate = (year, month, day) =>
  `${String(day).padStart(2, "0")}-${String(month).padStart(2, "0")}-${year}`;
const getMonthPeriodLabel = (monthKey) => {
  const [year, month] = String(monthKey || "").split("-").map(Number);
  if (!year || !month) return `Month: ${monthKey}`;
  const lastDay = new Date(year, month, 0).getDate();
  return `From ${displayDate(year, month, 1)} to ${displayDate(year, month, lastDay)}`;
};

const Page = styled.div`
  background: radial-gradient(circle at 20% 20%, #f5f8ff, #eef2ff 55%, #f5f7fb 100%);
  min-height: 100vh;
  padding: 18px;
`;


const buildOfflineMonthItemReport = async (restaurantId, selectedMonth) => {
  if (!isDesktopApp()) return null;
  const { productMap, productByNameMap, categoryMap: localCatMap } = await getLocalProductAndCategoryMaps(restaurantId);
  const [cachedOrders, pendingRows] = await Promise.all([
    window.desktopOrders.list({ restaurantId }),
    window.desktopOrderQueue?.list({ restaurantId, includeDeferred: true }) || [],
  ]);

  const orders = (cachedOrders || []).filter((order) =>
    indiaDateKey(order?.created_at ?? order?.createdAt ?? order?.date).startsWith(selectedMonth)
  );
  const pendingOrders = (pendingRows || [])
    .filter((entry) => indiaDateKey(entry?.createdAt).startsWith(selectedMonth))
    .map((entry) => ({
      id: `pending-${entry.id}`,
      created_at: entry.createdAt,
      itemlist: entry?.payload?.itemlist || [],
      payload: entry.payload,
      is_pending_sync: true,
    }));

  const allOrders = [...pendingOrders, ...orders];
  const aggregate = new Map();
  let ordersWithItems = 0;
  let missingDetailCount = 0;

  await Promise.all(allOrders.map(async (order) => {
    const detail = order?.is_pending_sync ? null : await getCachedOrderDetail(restaurantId, order);
    const items = extractOrderItems(detail, order);
    if (items.length) ordersWithItems += 1;
    else if (!order?.is_pending_sync) missingDetailCount += 1;

    const seenInOrder = new Set();
    items.forEach((item) => {
      const productId = item?.product_id ?? item?.productid ?? item?.id ?? "";
      const weightLabel = resolveItemWeightLabel(item);
      const itemName = stripWeightSuffix(
        item?.item_name ?? item?.product_name ?? item?.productname ?? item?.name ?? "Item",
        weightLabel
      );
      const weighted = isWeightedItem(item);
      const categoryName = getCategoryNameFromItem(item, productMap, productByNameMap, localCatMap);
      const key = `${String(productId || `${categoryName}:${itemName}`).toLowerCase()}:${weighted ? "weight" : "unit"}`;

      const quantity = numberValue(item?.total_quantity ?? item?.qty ?? item?.quantity ?? item?.qtyy);
      const lineAmount = item?.total_amount !== undefined
        ? numberValue(item.total_amount)
        : item?.amount !== undefined
        ? numberValue(item.amount)
        : quantity * numberValue(item?.priceaftertax ?? item?.taxedprice ?? item?.price);

      const current = aggregate.get(key) || {
        product_id: productId || key,
        item_name: itemName,
        category_name: categoryName,
        total_quantity: 0,
        total_orders: 0,
        total_amount: 0,
        is_weight_line: weighted ? 1 : 0,
        sold_weight_grams: 0,
        weight_label: "",
      };

      current.total_quantity += quantity;
      current.total_amount += lineAmount;
      if (weighted) {
        const grams = numberValue(item?.sold_weight_grams ?? item?.soldWeightGrams ?? 0);
        current.sold_weight_grams += grams > 0 ? grams : quantity * 1000;
        current.weight_label = formatSaleWeight(current.sold_weight_grams);
      }

      if (!seenInOrder.has(key)) {
        current.total_orders += 1;
        seenInOrder.add(key);
      }

      aggregate.set(key, current);
    });
  }));

  const items = [...aggregate.values()].sort((a, b) =>
    b.total_quantity - a.total_quantity || b.total_amount - a.total_amount
  );

  return {
    ...defaultData(selectedMonth),
    success: true,
    offline: true,
    total_items: items.length,
    total_quantity: items.reduce((sum, item) => sum + item.total_quantity, 0),
    total_amount: items.reduce((sum, item) => sum + item.total_amount, 0),
    total_orders: ordersWithItems,
    cached_order_count: orders.length,
    missing_detail_count: missingDetailCount,
    items,
  };
};

const offlineSourceMessage = (report) => {
  const missing = numberValue(report?.missing_detail_count);
  if (missing > 0) {
    return report?.full_offline
      ? `Full Offline — ${missing} saved sale${missing === 1 ? " has" : "s have"} no item details and could not be included.`
      : `Offline mode — ${missing} saved sale${missing === 1 ? " has" : "s have"} only header data. Re-run Offline Sales Sync to download item details.`;
  }
  if (report?.full_offline) {
    return "Full Offline — monthly item totals are calculated only from the isolated local database.";
  }
  return "Offline mode — item totals are calculated from the range-sync SQLite bills and pending orders.";
};

const ActionsRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const PrimaryBtn = styled.button`
  background: linear-gradient(135deg, var(--app-primary), var(--app-primary-hover));
  color: #fff;
  border: 0;
  border-radius: 10px;
  padding: 8px 14px;
  font-weight: 700;
  font-size: 0.8rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 4px 12px rgba(7, 95, 158, 0.15);
  transition: all 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(7, 95, 158, 0.25);
  }
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 18px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
`;

const Center = styled.div`
  text-align: center;
  color: #64748b;
  padding: 24px 0;
  font-weight: 600;
`;

const TableWrap = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    padding: 12px 14px;
    border-bottom: 1px solid #e2e8f0;
    text-align: left;
    font-size: 14px;
  }
  th {
    background: #f8fafc;
    color: #334155;
    font-weight: 800;
    text-transform: uppercase;
    font-size: 0.75rem;
    letter-spacing: 0.5px;
  }
  tr:hover td {
    background: #f8fafc;
  }
`;

const ItemName = styled.div`
  color: #1f2937;
  font-weight: 700;
  line-height: 1.35;
  overflow-wrap: anywhere;
`;

const ItemWeightLabel = styled.div`
  margin-top: 2px;
  color: #475569;
  font-size: 12px;
  font-weight: 700;
`;

const NumberCell = styled.td`
  text-align: right !important;
  font-variant-numeric: tabular-nums;
`;

const GrandTotalRow = styled.tr`
  td {
    background: #e8f3fc;
    color: #0f3f61;
    font-weight: 900;
    border-top: 2px solid var(--app-primary);
  }
`;

const Pager = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
  flex-wrap: wrap;
`;

const PageBtn = styled.button`
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 7px 14px;
  font-weight: 700;
  color: var(--app-primary);
  cursor: pointer;
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const PageInfo = styled.div`
  color: #64748b;
  font-size: 0.88rem;
`;
